import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import { dashboardApi, hrPipelineApi } from '../lib/api';
import { useDataRefresh, DATA_EVENTS } from '../contexts/DataRefreshContext';
import { useAuth } from '../contexts/AuthContext';
import { usePageVisitLogger, logClick } from '../hooks/useActivityLogger';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { StatusBadge, ScoreBadge } from '../components/common/Badges';
import ScoreRing from '../components/common/ScoreRing';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Users,
  Briefcase,
  Target,
  TrendingUp,
  Clock,
  ArrowRight,
  Loader2,
  UserCheck,
  Building2,
  DoorOpen,
  Calendar,
  Mail,
  ClipboardList,
  Sun,
  Sunrise,
  Moon,
  GitBranch,
  Trophy,
  Upload,
  Search,
  ThumbsUp,
  Phone,
  HandshakeIcon,
  UserX,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

// Requirement pipeline stages - the ONE vocabulary (RequirementDetailPage.js STAGE_CONFIG order).
// The strip tiles and the company table both render from this list; before 6 Sep 2026 the strip
// showed 7 stages (no Lined Up) and the table 6 in a different order (Lined Up before Screened).
const REQ_STAGES = [
  { key: 'uploaded',    label: 'Uploaded',    icon: Upload,        border: 'border-l-slate-400',  iconBg: 'bg-slate-100',  iconText: 'text-slate-600',  cell: 'bg-slate-200 text-slate-700 hover:bg-slate-300' },
  { key: 'screened',    label: 'Screened',    icon: Search,        border: 'border-l-blue-400',   iconBg: 'bg-blue-100',   iconText: 'text-blue-600',   cell: 'bg-blue-100 text-blue-700 hover:bg-blue-200' },
  { key: 'shortlisted', label: 'Shortlisted', icon: ThumbsUp,      border: 'border-l-cyan-400',   iconBg: 'bg-cyan-100',   iconText: 'text-cyan-600',   cell: 'bg-cyan-100 text-cyan-700 hover:bg-cyan-200' },
  { key: 'lined_up',    label: 'Lined Up',    icon: Calendar,      border: 'border-l-indigo-400', iconBg: 'bg-indigo-100', iconText: 'text-indigo-600', cell: 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200' },
  { key: 'interviewed', label: 'Interviewed', icon: Phone,         border: 'border-l-purple-400', iconBg: 'bg-purple-100', iconText: 'text-purple-600', cell: 'bg-purple-100 text-purple-700 hover:bg-purple-200' },
  { key: 'offered',     label: 'Offered',     icon: HandshakeIcon, border: 'border-l-amber-400',  iconBg: 'bg-amber-100',  iconText: 'text-amber-600',  cell: 'bg-amber-100 text-amber-700 hover:bg-amber-200' },
  { key: 'joined',      label: 'Joined',      icon: UserCheck,     border: 'border-l-green-400',  iconBg: 'bg-green-100',  iconText: 'text-green-600',  cell: 'bg-green-100 text-green-700 hover:bg-green-200' },
  { key: 'rejected',    label: 'Rejected',    icon: UserX,         border: 'border-l-red-400',    iconBg: 'bg-red-100',    iconText: 'text-red-600',    cell: 'bg-red-100 text-red-700 hover:bg-red-200' },
];
// /api/dashboard/requirement-candidates-stats names the first bucket "uploaded_not_screened".
const stageTotal = (totals, key) => (key === 'uploaded' ? totals?.uploaded_not_screened : totals?.[key]) || 0;

export default function DashboardPage() {
  // Activity logging
  usePageVisitLogger('Dashboard');
  
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPercentage, setSelectedPercentage] = useState('60');
  const [interviewCount, setInterviewCount] = useState(0);
  const [pipelineStats, setPipelineStats] = useState(null);
  const [hiredStats, setHiredStats] = useState(null);
  const { refreshTriggers } = useDataRefresh();
  const { user, canAccess } = useAuth();

  const [reqCandidatesStats, setReqCandidatesStats] = useState(null);
  const [taskStats, setTaskStats] = useState({ overdue: { count: 0, tasks: [] }, due_today: { count: 0, tasks: [] }, active: 0 });

  // Auto-refresh when data changes anywhere in the app
  useEffect(() => {
    loadStats();
    loadInterviewCount();
    loadPipelineStats();
    loadHiredStats();
    loadReqCandidatesStats();
    loadTaskStats();
  }, [refreshTriggers.dashboard, refreshTriggers[DATA_EVENTS.CANDIDATE_CREATED], 
      refreshTriggers[DATA_EVENTS.JOB_CREATED], refreshTriggers[DATA_EVENTS.MATCH_UPDATED]]);

  const loadPipelineStats = async () => {
    try {
      const response = await hrPipelineApi.getStats();
      setPipelineStats(response.data);
    } catch (error) {
      console.error('Failed to load pipeline stats:', error);
    }
  };

  const loadHiredStats = async () => {
    try {
      const response = await hrPipelineApi.getHiredStats();
      setHiredStats(response.data);
    } catch (error) {
      console.error('Failed to load hired stats:', error);
    }
  };

  const loadInterviewCount = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      const response = await fetch(`${API_URL}/api/interviews?status=scheduled&limit=1`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInterviewCount(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to load interview count:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await dashboardApi.stats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadReqCandidatesStats = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      const response = await fetch(`${API_URL}/api/dashboard/requirement-candidates-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReqCandidatesStats(data);
      }
    } catch (error) {
      console.error('Failed to load requirement candidates stats:', error);
    }
  };

  const loadTaskStats = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      const response = await fetch(`${API_URL}/api/manual-selections/tasks/dashboard/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTaskStats({
          overdue: { count: data.overdue || 0, tasks: [] },
          due_today: { count: data.due_today || 0, tasks: [] },
          active: data.active || 0,
        });
      }
    } catch (error) {
      console.error('Failed to load task stats:', error);
    }
  };

  if (loading) {
    return (
      <div>
        <Header title="Dashboard" subtitle="Loading..." />
        <div className="page-container flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  const pieColors = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  // Time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    const firstName = user?.full_name?.split(' ')[0] || 'there';
    if (hour >= 5 && hour < 12) {
      return { text: `Good Morning, ${firstName}`, icon: Sunrise };
    } else if (hour >= 12 && hour < 17) {
      return { text: `Good Afternoon, ${firstName}`, icon: Sun };
    } else {
      return { text: `Good Evening, ${firstName}`, icon: Moon };
    }
  };
  
  const greeting = getGreeting();
  const GreetingIcon = greeting.icon;

  return (
    <div>
      <Header 
        title={
          <span className="flex items-center gap-2">
            <GreetingIcon className="w-6 h-6 text-amber-500" />
            {greeting.text}
          </span>
        } 
        subtitle="" 
      />
      
      <div className="page-container">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <Link to="/ats/candidates?sort=highest_match_score" className="block">
            <Card className="card-hover cursor-pointer hover:shadow-lg transition-shadow" data-testid="stat-candidates">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Total Candidates</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">
                      {stats?.total_candidates || 0}
                    </p>
                    <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                      View All <ArrowRight className="w-3 h-3" />
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/ats/jobs?sort=openings" className="block">
            <Card className="card-hover cursor-pointer hover:shadow-lg transition-shadow" data-testid="stat-jobs">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Total Jobs</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">
                      {stats?.total_jobs || 0}
                    </p>
                    <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                      {stats?.open_jobs || 0} open <ArrowRight className="w-3 h-3" />
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                    <Briefcase className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Total Job Openings */}
          <Link to="/ats/jobs?sort=openings&status=open" className="block">
            <Card className="card-hover bg-gradient-to-br from-amber-50 to-orange-50 cursor-pointer hover:shadow-lg transition-shadow" data-testid="stat-openings">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-amber-700">Total Openings</p>
                    <p className="text-3xl font-bold text-amber-900 mt-1">
                      {stats?.total_job_openings || 0}
                    </p>
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                      Positions to fill <ArrowRight className="w-3 h-3" />
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <DoorOpen className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/ats/matches?sort=score" className="block" onClick={() => logClick('Dashboard Card', 'Total Matches')}>
            <Card className="card-hover cursor-pointer hover:shadow-lg transition-shadow" data-testid="stat-matches">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Total Matches</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">
                      {stats?.total_matches || 0}
                    </p>
                    <p className="text-xs text-purple-600 mt-1 flex items-center gap-1">
                      View Best Matches <ArrowRight className="w-3 h-3" />
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
                    <Target className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/ats/manual-selections/interviews" className="block">
            <Card className="card-hover bg-gradient-to-br from-teal-50 to-green-50 cursor-pointer hover:shadow-lg transition-shadow" data-testid="stat-interviews">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-teal-700">Interviews Scheduled</p>
                    <p className="text-3xl font-bold text-teal-900 mt-1">
                      {interviewCount}
                    </p>
                    <p className="text-xs text-teal-600 mt-1 flex items-center gap-1">
                      View Interviews <ArrowRight className="w-3 h-3" />
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-teal-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Candidates in Journey - HR Pipeline */}
          <Link to="/ats/hr-pipeline" className="block" onClick={() => setTimeout(() => document.querySelector('[data-testid="timeline-tab"]')?.click(), 500)}>
            <Card className="card-hover bg-gradient-to-br from-indigo-50 to-purple-50 cursor-pointer hover:shadow-lg transition-shadow" data-testid="stat-in-journey">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-indigo-700">Candidates in Journey</p>
                    <p className="text-3xl font-bold text-indigo-900 mt-1">
                      {pipelineStats?.in_journey || 0}
                    </p>
                    <p className="text-xs text-indigo-600 mt-1 flex items-center gap-1">
                      {pipelineStats?.pending || 0} pending, {pipelineStats?.shortlisted || 0} shortlisted <ArrowRight className="w-3 h-3" />
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                    <GitBranch className="w-6 h-6 text-indigo-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Hired Candidates Widget */}
          <Link to="/ats/hired-candidates" className="block">
            <Card className="card-hover bg-gradient-to-br from-green-50 to-emerald-50 cursor-pointer hover:shadow-lg transition-shadow" data-testid="stat-hired">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-700">Candidates Hired</p>
                    <p className="text-3xl font-bold text-green-900 mt-1">
                      {hiredStats?.total_hired || 0}
                    </p>
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      {hiredStats?.hired_this_month || 0} this month <ArrowRight className="w-3 h-3" />
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Pending Email Approvals - Admin Only */}
          {canAccess('admin') && stats?.pending_email_approvals > 0 && (
            <Link to="/ats/settings" className="block">
              <Card className="card-hover bg-gradient-to-br from-red-50 to-orange-50 cursor-pointer hover:shadow-lg transition-shadow border-red-200" data-testid="stat-email-approvals">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-red-700">Pending Email Approvals</p>
                      <p className="text-3xl font-bold text-red-900 mt-1">
                        {stats.pending_email_approvals}
                      </p>
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        Review & Approve <ArrowRight className="w-3 h-3" />
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                      <Mail className="w-6 h-6 text-red-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          )}

          {/* Pending Tasks (real Tasks module, ms_tasks) */}
          {taskStats.active > 0 && (
            <Link to="/ats/manual-selections/tasks" className="block">
              <Card className="card-hover bg-gradient-to-br from-purple-50 to-pink-50 cursor-pointer hover:shadow-lg transition-shadow" data-testid="stat-pending-todos">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-purple-700">My Pending Tasks</p>
                      <p className="text-3xl font-bold text-purple-900 mt-1">
                        {taskStats.active}
                      </p>
                      <p className="text-xs text-purple-600 mt-1 flex items-center gap-1">
                        View Tasks <ArrowRight className="w-3 h-3" />
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <ClipboardList className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          )}

          {/* My Tasks Widget - Overdue + Due Today */}
          {(taskStats.overdue.count > 0 || taskStats.due_today.count > 0) && (
            <Link to="/ats/manual-selections/tasks" className="block">
              <Card className={`card-hover cursor-pointer hover:shadow-lg transition-shadow ${
                taskStats.overdue.count > 0 ? 'bg-gradient-to-br from-red-50 to-orange-50 border-red-200' : 'bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-200'
              }`} data-testid="stat-tasks-widget">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm font-medium ${taskStats.overdue.count > 0 ? 'text-red-700' : 'text-yellow-700'}`}>
                        {taskStats.overdue.count > 0 ? 'Overdue Tasks' : 'Due Today'}
                      </p>
                      <p className={`text-3xl font-bold mt-1 ${taskStats.overdue.count > 0 ? 'text-red-900' : 'text-yellow-900'}`}>
                        {taskStats.overdue.count > 0 ? taskStats.overdue.count : taskStats.due_today.count}
                      </p>
                      <p className={`text-xs mt-1 flex items-center gap-1 ${taskStats.overdue.count > 0 ? 'text-red-600' : 'text-yellow-600'}`}>
                        {taskStats.due_today.count > 0 && taskStats.overdue.count > 0 ? `+ ${taskStats.due_today.count} due today` : 'View Tasks'} <ArrowRight className="w-3 h-3" />
                      </p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${taskStats.overdue.count > 0 ? 'bg-red-100' : 'bg-yellow-100'}`}>
                      <Clock className={`w-6 h-6 ${taskStats.overdue.count > 0 ? 'text-red-600' : 'text-yellow-600'}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          )}
        </div>

        {/* Requirement Candidates Pipeline Strip */}
        {reqCandidatesStats?.stage_totals?.total > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Users className="w-5 h-5 text-slate-600" />
                Requirement Pipeline
              </h2>
              <Link 
                to="/ats/requirements" 
                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
              >
                View All Requirements <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            {/* Stage Tiles Strip - one vocabulary: REQ_STAGES (the Requirements page's STAGE_CONFIG) */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
              {REQ_STAGES.map((st) => {
                const Icon = st.icon;
                return (
                  <Link
                    key={st.key}
                    to={`/ats/requirements?stage=${st.key}`}
                    className="block"
                    data-testid={`stage-tile-${st.key}`}
                  >
                    <Card className={`hover:shadow-md transition-shadow cursor-pointer border-l-4 ${st.border}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 ${st.iconBg} rounded-lg flex items-center justify-center`}>
                            <Icon className={`w-5 h-5 ${st.iconText}`} />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-slate-800">
                              {stageTotal(reqCandidatesStats.stage_totals, st.key)}
                            </p>
                            <p className="text-xs text-slate-500">{st.label}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>

            {/* Company-wise Requirement Table - same REQ_STAGES, same order, same labels */}
            {reqCandidatesStats?.table_data?.length > 0 && (
              <Card className="mt-6">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-slate-500" />
                    Candidates by Company & Requirement
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-y">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium text-slate-600">Company / Requirement</th>
                          {REQ_STAGES.map((st) => (
                            <th key={st.key} className="px-3 py-2 text-center font-medium text-slate-600">{st.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {reqCandidatesStats.table_data.map((company) => (
                          <React.Fragment key={`company-group-${company.client_id}`}>
                            {/* Company Header Row */}
                            <tr className="bg-slate-100">
                              <td colSpan={REQ_STAGES.length + 1} className="px-4 py-2 font-semibold text-slate-700">
                                <div className="flex items-center gap-2">
                                  <Building2 className="w-4 h-4 text-slate-500" />
                                  {company.company_name}
                                </div>
                              </td>
                            </tr>
                            {/* Requirement Rows */}
                            {company.requirements.map((req) => (
                              <tr key={req.requirement_id} className="hover:bg-slate-50">
                                <td className="px-4 py-2 pl-8">
                                  <Link
                                    to={`/ats/requirements/${req.requirement_id}`}
                                    className="text-blue-600 hover:underline"
                                  >
                                    {req.position_title}
                                  </Link>
                                </td>
                                {REQ_STAGES.map((st) => (
                                  <td key={st.key} className="px-3 py-2 text-center">
                                    <Link
                                      to={`/ats/requirements/${req.requirement_id}?stage=${st.key}`}
                                      className={`inline-block min-w-[32px] px-2 py-0.5 rounded text-xs font-medium ${
                                        req[st.key] > 0 ? st.cell : 'text-slate-400'
                                      }`}
                                    >
                                      {req[st.key] || '-'}
                                    </Link>
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Top Matches - Hero */}
          <Card className="md:col-span-2 row-span-2" data-testid="top-matches-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold">Top Matches</CardTitle>
                <Link 
                  to="/ats/matches" 
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  data-testid="view-all-matches"
                >
                  View all <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats?.top_matches?.length > 0 ? (
                  stats.top_matches.map((match) => (
                    <Link
                      key={match.id}
                      to={`/ats/matches/${match.id}`}
                      className="flex items-center gap-4 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                      data-testid={`match-${match.id}`}
                    >
                      <ScoreRing score={match.overall_score} size="sm" showLabel={false} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {match.candidate_name}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {match.job_title}
                        </p>
                      </div>
                      <ScoreBadge score={match.overall_score} showLabel={false} />
                    </Link>
                  ))
                ) : (
                  <p className="text-sm text-slate-500 text-center py-8">
                    No matches yet. Upload CVs and Jobs to get started.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Skills Distribution */}
          <Card className="md:col-span-2" data-testid="skills-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold">Top Skills</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.skills_distribution?.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={stats.skills_distribution.slice(0, 6)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                    <YAxis 
                      dataKey="_id" 
                      type="category" 
                      tick={{ fontSize: 11 }} 
                      stroke="#94a3b8"
                      width={80}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        background: '#fff', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}
                    />
                    <Bar dataKey="count" fill="#2563eb" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-slate-500 text-center py-8">No skills data yet</p>
              )}
            </CardContent>
          </Card>

          {/* Company Jobs Distribution - % Scope */}
          <Card className="md:col-span-2" data-testid="company-jobs-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Building2 className="w-5 h-5 text-indigo-600" />
                Job Openings by Company
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.company_jobs_distribution?.length > 0 ? (
                <div className="space-y-3">
                  {stats.company_jobs_distribution.map((company, idx) => (
                    <div key={company._id || idx} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-slate-700 truncate max-w-[60%]">
                          {company.company_name || 'Unknown Company'}
                        </span>
                        <div className="flex items-center gap-3">
                          <span className="text-slate-500">{company.total_openings} openings</span>
                          <span className="font-bold text-indigo-600">{company.percentage}%</span>
                        </div>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all"
                          style={{ width: `${company.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                  
                  {stats.jobs_without_company > 0 && (
                    <div className="mt-4 pt-3 border-t border-slate-100">
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        ⚠️ {stats.jobs_without_company} job(s) not assigned to any company
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-sm">No company job distribution yet</p>
                  <p className="text-xs mt-1">Assign companies to jobs to see the breakdown</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Candidates */}
          <Card data-testid="recent-candidates-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold">Recent Candidates</CardTitle>
                <Link 
                  to="/ats/candidates" 
                  className="text-sm text-blue-600 hover:text-blue-700"
                  data-testid="view-all-candidates"
                >
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stats?.recent_candidates?.length > 0 ? (
                  stats.recent_candidates.map((c) => (
                    <Link
                      key={c.id}
                      to={`/ats/candidates/${c.id}`}
                      className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 transition-colors"
                      data-testid={`recent-candidate-${c.id}`}
                    >
                      <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-slate-600">
                          {c.name?.charAt(0) || '?'}
                        </span>
                      </div>
                      <span className="text-sm text-slate-700 truncate flex-1">{c.name}</span>
                    </Link>
                  ))
                ) : (
                  <p className="text-xs text-slate-500 text-center py-4">No candidates yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Jobs */}
          <Card data-testid="recent-jobs-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold">Recent Jobs</CardTitle>
                <Link 
                  to="/ats/jobs" 
                  className="text-sm text-blue-600 hover:text-blue-700"
                  data-testid="view-all-jobs"
                >
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stats?.recent_jobs?.length > 0 ? (
                  stats.recent_jobs.map((j) => (
                    <Link
                      key={j.id}
                      to={`/ats/jobs/${j.id}`}
                      className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 transition-colors"
                      data-testid={`recent-job-${j.id}`}
                    >
                      <Briefcase className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-700 truncate flex-1">{j.job_title}</span>
                    </Link>
                  ))
                ) : (
                  <p className="text-xs text-slate-500 text-center py-4">No jobs yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Status Distribution */}
          <Card className="md:col-span-2" data-testid="status-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold">Candidate Status</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.candidate_status?.length > 0 ? (
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width="50%" height={150}>
                    <PieChart>
                      <Pie
                        data={stats.candidate_status}
                        dataKey="count"
                        nameKey="_id"
                        cx="50%"
                        cy="50%"
                        innerRadius={30}
                        outerRadius={60}
                      >
                        {stats.candidate_status.map((entry, index) => (
                          <Cell key={entry._id} fill={pieColors[index % pieColors.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          background: '#fff', 
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '12px'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-1">
                    {stats.candidate_status.map((s, i) => (
                      <div key={s._id} className="flex items-center gap-2 text-xs">
                        <div 
                          className="w-3 h-3 rounded" 
                          style={{ backgroundColor: pieColors[i % pieColors.length] }}
                        />
                        <span className="text-slate-600 capitalize">{s._id || 'Unknown'}</span>
                        <span className="text-slate-400 ml-auto">{s.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-8">No status data yet</p>
              )}
            </CardContent>
          </Card>

          {/* Match Score Distribution */}
          <Card className="md:col-span-2" data-testid="score-distribution-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold">Match Score Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.score_distribution?.length > 0 ? (
                <ResponsiveContainer width="100%" height={150}>
                  <BarChart data={stats.score_distribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="range" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <Tooltip 
                      contentStyle={{ 
                        background: '#fff', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}
                    />
                    <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-slate-500 text-center py-8">No matches yet</p>
              )}
            </CardContent>
          </Card>

          {/* Matching Resumes by Percentage */}
          <Link to="/ats/matches" className="md:col-span-2">
            <Card className="hover:shadow-md transition-shadow cursor-pointer h-full" data-testid="matching-resumes-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <UserCheck className="w-5 h-5 text-emerald-600" />
                    Matching Resumes
                  </CardTitle>
                  <div onClick={(e) => e.preventDefault()}>
                    <Select value={selectedPercentage} onValueChange={setSelectedPercentage}>
                      <SelectTrigger className="w-[100px] h-8">
                        <SelectValue placeholder="Select %" />
                      </SelectTrigger>
                      <SelectContent>
                        {[100, 90, 80, 70, 60, 50, 40, 30, 20, 10].map((pct) => (
                          <SelectItem key={pct} value={String(pct)}>
                            ≥ {pct}%
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {stats?.matching_by_percentage?.length > 0 ? (
                  <div className="space-y-3">
                    {/* Main stat */}
                    <div className="text-center py-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl">
                      <p className="text-5xl font-bold text-emerald-600">
                        {stats.matching_by_percentage.find(m => m.percentage === parseInt(selectedPercentage))?.candidate_count || 0}
                      </p>
                      <p className="text-sm text-slate-600 mt-1">
                        Candidates with ≥{selectedPercentage}% match
                      </p>
                      <p className="text-xs text-blue-600 mt-2 flex items-center justify-center gap-1">
                        <ArrowRight className="w-3 h-3" />
                        Click to view all
                      </p>
                    </div>
                    
                    {/* Quick stats */}
                    <div className="grid grid-cols-5 gap-2 text-center" onClick={(e) => e.preventDefault()}>
                      {stats.matching_by_percentage.filter(m => [90, 70, 50, 30, 10].includes(m.percentage)).map((m) => (
                        <div 
                          key={m.percentage}
                          onClick={(e) => { e.stopPropagation(); setSelectedPercentage(String(m.percentage)); }}
                          className={`p-2 rounded-lg cursor-pointer transition-all ${
                            parseInt(selectedPercentage) === m.percentage 
                              ? 'bg-emerald-100 ring-2 ring-emerald-400' 
                              : 'bg-slate-50 hover:bg-slate-100'
                          }`}
                        >
                          <p className="text-lg font-bold text-slate-700">{m.candidate_count}</p>
                          <p className="text-xs text-slate-500">≥{m.percentage}%</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-8">No matching data yet</p>
                )}
              </CardContent>
            </Card>
          </Link>

          {/* Monthly Trend */}
          <Card className="md:col-span-2" data-testid="monthly-trend-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold">Monthly Activity</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.monthly_trend?.length > 0 ? (
                <ResponsiveContainer width="100%" height={150}>
                  <BarChart data={stats.monthly_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <Tooltip 
                      contentStyle={{ 
                        background: '#fff', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}
                    />
                    <Bar dataKey="candidates" fill="#2563eb" name="Candidates" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="jobs" fill="#10b981" name="Jobs" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-slate-500 text-center py-8">No activity data yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
